import React, { useEffect, useState } from "react";
import SitesFilter from "../views/Sites/SitesFilter";
import { useDispatch, useSelector } from "../../store";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import {
  deleteSiteType,
  getSiteTypeId,
  resetSiteType,
  searchSiteType,
  setSiteTypeFilter,
} from "../../slices/siteType";
import KeyValue from "../../types/keyValue";
import SearchParams, { defaultParams } from "../../types/searchParams";
import {
  deleteSite,
  getSiteId,
  resetSite,
  searchSite,
  setSiteFilter,
} from "../../slices/site";
import SitesTable from "../views/Sites/SitesTable";
import { Base } from "../base";
import { useNavigate } from "react-router-dom";
import {
  deleteSiteManager,
  getSiteManagerId,
  resetSiteManager,
  searchSiteManager,
  setSiteManagerFilter,
} from "../../slices/siteManager";
import {
  LocationCity,
  ManageAccounts,
  RoomPreferences,
} from "@mui/icons-material";
import TabNavButton from "../../types/tabNavButton";
import SiteManagers from "../views/Settings/SiteManagers";
import SettingsModal from "../../types/settingsModal";
import SiteTypes from "../views/Settings/SiteTypes";

interface Props {}

const SitesPage: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { siteFilter, site, status } = useSelector((store) => store.site);
  const { siteManager, status: siteManagerStatus } = useSelector(
    (store) => store.siteManager
  );
  const { siteType, status: siteTypeStatus } = useSelector(
    (store) => store.siteType
  );
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [highlightId, setHighlightId] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteModal, setDeleteModal] = useState<SettingsModal>({
    visible: false,
  });

  useEffect(() => {
    dispatch(searchSiteType(defaultParams));
    if (!xs) {
      dispatch(searchSite(defaultParams));
    }
  }, [dispatch, xs]);

  useEffect(() => {
    if (!xs) {
      switch (activeTab) {
        case 0: //sites
          dispatch(searchSite(defaultParams));
          dispatch(setSiteFilter(defaultParams));
          break;
        case 1: // site managers
          dispatch(searchSiteManager(defaultParams));
          dispatch(setSiteManagerFilter(defaultParams));
          break;
        case 2: // site type
          dispatch(searchSiteType(defaultParams));
          dispatch(setSiteTypeFilter(defaultParams));
          break;
        default:
          break;
      }
    }
  }, [dispatch, activeTab, xs]);

  const siteSearch = async (search: SearchParams) => {
    await dispatch(searchSite(search)).then((result: any) => {
      dispatch(setSiteFilter(search));
      setMobileRows(result.payload.results);
    });
  };

  const handleFiltering = (filterName: string, e?: KeyValue) => {
    let filters = siteFilter?.filters;
    let otherFilters = filters?.filter((f) => f.split(":")[0] !== filterName);

    if (e) {
      let newFilters: string[] = [`${filterName}:${e.id}`];
      let newList = otherFilters ? newFilters.concat(otherFilters) : newFilters;
      updateFilters(newList);
    } else {
      updateFilters(otherFilters!);
    }
  };

  const updateFilters = (filterList: string[]) => {
    dispatch(
      setSiteFilter({
        ...siteFilter,
        pageNumber: xs ? 2 : 1,
        pageSize: 10,
        filters: filterList,
      })
    );
    dispatch(
      searchSite({
        ...siteFilter,
        pageNumber: 1,
        pageSize: 10,
        filters: filterList,
      })
    ).then((result: any) => {
      setMobileRows(result.payload.results);
    });
  };

  const closeDelete = () => {
    setDeleteModal({ visible: false, modal: undefined });
    dispatch(resetSite());
    dispatch(resetSiteManager());
    dispatch(resetSiteType());
    setHighlightId(0);
  };

  const next = () => {
    dispatch(searchSite(siteFilter)).then((result: any) => {
      dispatch(
        setSiteFilter({
          ...siteFilter,
          pageNumber: siteFilter?.pageNumber! + 1,
        })
      );
      setMobileRows([...mobileRows, ...result.payload.results]);
    });
  };

  const handleNewSite = () => {
    dispatch(resetSite());
    navigate("/site/edit/0");
    dispatch(setSiteFilter(defaultParams));
  };

  const handleSearch = async (
    search: SearchParams,
    type: "siteManager" | "siteType"
  ) => {
    switch (type) {
      case "siteManager":
        const siteManagerResult: any = await dispatch(
          searchSiteManager(search)
        );
        if (siteManagerResult.payload.results) {
          return siteManagerResult.payload.results;
        } else return [];
      case "siteType":
        const siteTypeResult: any = await dispatch(searchSiteType(search));
        if (siteTypeResult.payload.results) {
          return siteTypeResult.payload.results;
        } else return [];

      default:
        break;
    }
  };

  const tabs: TabNavButton[] = [
    {
      id: 0,
      label: "Sites",
      element: (
        <Grid container>
          <Grid size={{ xs: 12 }} pb={{ sm: 0, xs: 2 }}>
            <SitesFilter
              apiSiteTypeSearch={(search: SearchParams) => {
                dispatch(searchSiteType(search));
              }}
              handleFiltering={handleFiltering}
              apiSiteSearch={siteSearch}
              setFilter={(params: SearchParams) => {
                dispatch(setSiteFilter(params));
              }}
              phone={xs}
            />
          </Grid>
          <Grid size={{ xs: 12 }} pb={{ xs: 15, md: 5 }}>
            <SitesTable
              apiGetSite={(id: number) => {
                dispatch(getSiteId(id));
              }}
              mobileRows={mobileRows}
              next={next}
              apiSiteSearch={siteSearch}
              setFilter={(params: SearchParams) => {
                dispatch(setSiteFilter(params));
              }}
              phone={xs}
              setDeleteModal={setDeleteModal}
              setHighlightId={setHighlightId}
              highlightId={highlightId}
              onClickNewSite={handleNewSite}
            />
          </Grid>
        </Grid>
      ),
      icon: <LocationCity />,
    },
    {
      id: 1,
      label: "Site Managers",
      element: (
        <SiteManagers
          apiSiteManagerSearch={(search) => handleSearch(search, "siteManager")}
          phone={xs}
          setDeleteModal={setDeleteModal}
          apiGetSiteManager={(id: number) => {
            dispatch(getSiteManagerId(id));
          }}
          resetSiteManager={() => {
            dispatch(resetSiteManager());
          }}
          setSiteManagerFilter={(params: SearchParams) => {
            dispatch(setSiteManagerFilter(params));
          }}
        />
      ),
      icon: <ManageAccounts />,
    },
    {
      id: 2,
      label: "Site Types",
      element: (
        <SiteTypes
          apiSiteTypeSearch={(search) => handleSearch(search, "siteType")}
          phone={xs}
          setDeleteModal={setDeleteModal}
          apiGetSiteType={(id: number) => {
            dispatch(getSiteTypeId(id));
          }}
          resetSiteType={() => {
            dispatch(resetSiteType());
          }}
          setSiteTypeFilter={(params: SearchParams) => {
            dispatch(setSiteTypeFilter(params));
          }}
        />
      ),
      icon: <RoomPreferences />,
    },
  ];

  const deleteSiteManagerMessage = `Are you sure you would like to delete ${siteManager.firstName} ${siteManager.lastName}?`;
  const deleteSiteTypeMessage = `Are you sure you would like to delete ${siteType.name}?`;
  const deleteSiteMessage = `Are you sure you would like to delete ${site.name}? This cannot be undone and all jobs and attachments tied to it will be lost.`;

  const handleDelete = async () => {
    switch (deleteModal.modal) {
      case "site":
        await dispatch(deleteSite(site.id)).then(() => {
          dispatch(searchSite(defaultParams)).then((result: any) => {
            if (result.meta.requestStatus === "fulfilled") {
              setMobileRows(result.payload.results);
            }
          });
        });
        dispatch(setSiteFilter({ query: "" }));
        setDeleteModal({ visible: false, modal: undefined });
        dispatch(resetSite());
        setHighlightId(0);
        break;
      case "siteManagers":
        dispatch(deleteSiteManager(siteManager.id)).then(() => {
          dispatch(searchSiteManager(defaultParams));
          dispatch(resetSiteManager());
        });
        break;

      case "siteType":
        dispatch(deleteSiteType(siteType.id)).then(() => {
          dispatch(searchSiteType(defaultParams));
          dispatch(resetSiteType());
        });
        break;
      default:
        break;
    }
    await setDeleteModal({ visible: false, modal: undefined });
  };

  return (
    <Box
      sx={{
        margin: { md: 5, xs: 0 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Base.TitleBar title={tabs[activeTab].label} />
      <Base.TabNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
      />
      <Base.Dialog
        open={deleteModal.visible}
        title={
          deleteModal.modal === "site"
            ? deleteSiteMessage
            : deleteModal.modal === "siteManagers"
            ? deleteSiteManagerMessage
            : deleteModal.modal === "siteType"
            ? deleteSiteTypeMessage
            : ""
        }
        handleCancel={closeDelete}
        confirmText="Delete"
        confirmButtonType="delete"
        handleConfirm={handleDelete}
        loading={
          deleteModal.modal === "site"
            ? status === "loading"
            : deleteModal.modal === "siteManagers"
            ? siteManagerStatus === "loading"
            : deleteModal.modal === "siteType"
            ? siteTypeStatus === "loading"
            : false
        }
      />
    </Box>
  );
};

export default SitesPage;
